import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { CurrencyTextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/CurrencyTextFieldAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option, StatusOption } from 'shared/types/general';
import { EquipmentFormModel, EquipmentFormValues } from '../../types';
import { equipmentService } from '../../api/equipment.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { SectionHeader } from 'components/ui/typography';
import { Spinner } from 'components/ui/data-display';

interface EquipmentFormParams {
  equipment: EquipmentFormModel;
  onSubmit: (values: EquipmentFormValues) => Promise<void> | void;
  disableEdit?: boolean;
}

const EquipmentForm = (params: EquipmentFormParams) => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const { equipment, onSubmit, disableEdit } = params;

  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [ivdrOptions, setIvdrOptions] = useState<StatusOption[]>([]);
  const [warehouses, setWarehouses] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [optionsRes, warehouseRes] = await Promise.all([
          equipmentService.options(),
          warehouseService.getAllBaseInformation()
        ]);

        if (optionsRes?.actions?.POST) {
          setStatusOptions(
            optionsRes.actions.POST.status.choices.map((item: any) => ({
              ...item,
              display_name: t(`${translationPrefix}.status_options.${item.display_name}`)
            }))
          );
          setIvdrOptions(optionsRes.actions.POST.ivdr_class.choices);
        }

        setWarehouses(warehouseRes || []);
      } catch (error) {
        console.error('Errore nel caricamento delle opzioni della form', error);
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, []);

  const selectedWarehouse = useMemo(() => {
    if (!equipment?.warehouse || warehouses.length === 0) return null;
    return warehouses.find((s) => s.id === equipment.warehouse) || null;
  }, [warehouses, equipment?.warehouse]);

  if (loading || !equipment) {
    return <Spinner />;
  }

  return (
    <>
      {equipment && (
        <EnhancedForm
          onSubmit={disableEdit ? () => Promise.resolve() : onSubmit}
          edit={!!equipment.id}
          disableEdit={disableEdit}
          autoStyleLayout
          columns={4}>
          <SectionHeader title={t('global.base-info')} gridSpan />

          <IdentityField
            name="code"
            defaultValue={equipment.code}
            component={TextFieldAdapter}
            label={t('global.code')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="internal_id"
            defaultValue={equipment.internal_id}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.internal_id`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={2}
            name="description"
            defaultValue={equipment.description}
            component={TextFieldAdapter}
            label={t('global.description')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="serial_number"
            defaultValue={equipment.serial_number}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.serial_number`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="manufacturer"
            defaultValue={equipment.manufacturer}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.manufacturer`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="status"
            label={t(`${translationPrefix}.status`)}
            initValue={
              equipment.status
                ? {
                    ...equipment.status,
                    display_name: t(
                      `${translationPrefix}.status_options.${equipment.status.display_name}`
                    )
                  }
                : {
                    value: 1,
                    display_name: t(`${translationPrefix}.status_options.NEW`)
                  }
            }
            defaultValue={equipment.status ? equipment.status.value : 1}
            component={AutocompleteAdapter}
            options={statusOptions}
            placeholder="Status"
            type="text"
            optionLabel="display_name"
            optionValue="value"
            disabled={disableEdit}
          />
          <IdentityField
            name="ivdr_class"
            label={t(`${translationPrefix}.ivdr_class`)}
            initValue={equipment.ivdr_class ?? null}
            defaultValue={equipment.ivdr_class ? equipment.ivdr_class['value'] : null}
            component={AutocompleteAdapter}
            options={ivdrOptions}
            type="text"
            optionLabel="display_name"
            optionValue="value"
            disabled={disableEdit}
          />
          <IdentityField
            name="supplier"
            defaultValue={equipment.supplier}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.supplier`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="position"
            defaultValue={equipment.position}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.position`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="work_island"
            defaultValue={equipment.work_island}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.isola_di_lavoro`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="instrument_type"
            defaultValue={equipment.instrument_type}
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.tipologia_strumento`)}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            name="warehouse"
            component={AutocompleteAdapter}
            initValue={selectedWarehouse}
            defaultValue={equipment.warehouse}
            options={warehouses}
            optionLabel="id"
            label={t('global.warehouse')}
            disabled={disableEdit}
            type="text"
          />
          <IdentityField
            name="market_value"
            format={(value) => value && value.replace('.', ',')}
            defaultValue={equipment.market_value}
            label={t(`${translationPrefix}.market_value`)}
            component={CurrencyTextFieldAdapter}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="notes"
            label={t('global.notes')}
            initialValue={equipment.notes}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />

          <SectionHeader title={t('global.misc')} gridSpan />
          <IdentityField
            gridSpan={2}
            name="transport_document_ref"
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.transport_document_ref`)}
            type="text"
            initialValue={equipment?.transport_document_ref ?? ''}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={2}
            name="delivery_date"
            component={DatePickerAdapter}
            label={t(`${translationPrefix}.delivery_date`)}
            type="date"
            initialValue={equipment?.delivery_date ?? null}
            disabled={disableEdit}
          />
          <IdentityField
            name="installation_date"
            component={DatePickerAdapter}
            label={t(`${translationPrefix}.installation_date`)}
            type="date"
            initialValue={equipment?.installation_date ?? null}
            disabled={disableEdit}
          />
          <IdentityField
            name="testing_date"
            component={DatePickerAdapter}
            label={t(`${translationPrefix}.testing_date`)}
            type="date"
            initialValue={equipment?.testing_date ?? null}
            disabled={disableEdit}
          />
          <IdentityField
            name="buying_date"
            component={DatePickerAdapter}
            label={t(`${translationPrefix}.buying_date`)}
            type="date"
            disabled={disableEdit}
            initialValue={equipment?.buying_date ?? null}
          />
          <IdentityField
            name="decommission_date"
            component={DatePickerAdapter}
            label={t(`${translationPrefix}.decommission_date`)}
            type="date"
            initialValue={equipment?.decommission_date ?? null}
            disabled={disableEdit}
          />

          {/* ===== Email / Contact ===== */}

          <SectionHeader title={t('pages.tickets.section-email')} gridSpan />
          <IdentityField
            gridSpan={4}
            name="ticketing_email"
            defaultValue={equipment.ticketing_email}
            component={TextFieldAdapter}
            label={t('global.ticketing_email')}
            required={false}
            type="text"
            tooltip="Indirizzo e-mail principale utilizzato dal sistema per l'apertura automatica o l'invio delle notifiche dei ticket di assistenza."
          />
          <IdentityField
            gridSpan={4}
            name="cc_ticketing_email"
            label={t(`${translationPrefix}.cc_ticketing_email`)}
            placeholder="mario.verdi@mail.it; francesco.bianchi@mail.it; franco.rossi@mail.it"
            initialValue={equipment.cc_ticketing_email}
            component={TextFieldAdapter}
            type="text"
          />
          <IdentityField
            gridSpan={4}
            name="assistance_contact_number"
            label={t(`${translationPrefix}.assistance_contact_number`)}
            initialValue={equipment.assistance_contact_number}
            component={TextFieldAdapter}
            type="text"
            tooltip="Numero telefonico di riferimento del servizio clienti del produttore o del tecnico dedicato alle urgenze sul campo."
          />
        </EnhancedForm>
      )}
    </>
  );
};

export default EquipmentForm;
